import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories } from "@/mocks/categories";
import { getWebPageJsonLd } from "@/utils/seo";

export default function ShoppingGuide() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getWebPageJsonLd(
              "/shopping-guide",
              "ACBuySpreadsheet Shopping Guide",
              "Browse our complete collection of premium fashion categories. Each category links directly to our main store at kaspreadsheet.com where you can explore hundreds of curated products."
            )
          ),
        }}
      />
      <Navbar />
      <main>
        {/* Header */}
        <section className="w-full pt-24 md:pt-28 pb-10 md:pb-14 bg-background-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto text-center">
              <p className="text-xs md:text-sm text-foreground-500 font-medium tracking-wide mb-3">
                / SHOPPING GUIDE
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-4">
                ACBuySpreadsheet Shopping Guide
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                Browse our complete collection of premium fashion categories. Each category links directly to our main store at <strong>kaspreadsheet.com</strong> where you can explore hundreds of curated products.
              </p>
            </div>
          </div>
        </section>

        {/* Categories grid with images */}
        <section className="w-full py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={cat.imageUrl}
                        alt={`${cat.name} shopping guide — ACBuySpreadsheet`}
                        title={`ACBuySpreadsheet ${cat.name} Shopping Guide`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                        {cat.name}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-3">{cat.itemCount}</p>
                      <div className="mt-auto flex items-center gap-2 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                        <span>Browse Collection</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How to shop section */}
        <section className="w-full py-12 md:py-16 bg-background-100/50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  How It Works
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  Shopping through ACBuySpreadsheet is simple and secure. Every category links you directly to our trusted main store.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                {[
                  { step: "01", icon: "ri-search-line", title: "Browse Categories", desc: "Explore our 14 fashion categories with detailed product images and descriptions. Find exactly what you are looking for." },
                  { step: "02", icon: "ri-external-link-line", title: "Visit Main Store", desc: "Click any category to be redirected to kaspreadsheet.com where you can view full product listings and pricing." },
                  { step: "03", icon: "ri-shopping-cart-2-line", title: "Secure Checkout", desc: "Complete your purchase on our main site with secure payment processing and worldwide shipping options." },
                ].map((item) => (
                  <div key={item.step} className="bg-background-50 rounded-2xl p-5 md:p-6 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-primary-100/70 flex items-center justify-center">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <span className="text-xs font-bold text-primary-500 tracking-wider">{item.step}</span>
                    <h4 className="text-sm font-semibold text-foreground-900 mt-2 mb-1">{item.title}</h4>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Ready to Start Shopping?
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                Browse our full collection at the main store. Thousands of premium fashion items waiting for you.
              </p>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 text-base font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Visit Main Store
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}